import { useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { updateCoverLetter } from "./cover-letter";

export const useDebouncedUpdateCoverLetter = (resumeId: string, delay = 1000) => {
  const queryClient = useQueryClient();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedUpdate = useCallback(
    (content: string) => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set a new timeout
      timeoutRef.current = setTimeout(async () => {
        try {
          const updatedCoverLetter = await updateCoverLetter(resumeId, { content });

          // Update the query cache
          queryClient.setQueryData(["cover-letter", resumeId], updatedCoverLetter);
          queryClient.invalidateQueries({ queryKey: ["resumes"] });
        } catch (error) {
          console.error("Failed to update cover letter:", error);
          // Optionally handle the error - could show a notification or retry
        }
      }, delay);
    },
    [resumeId, delay, queryClient],
  );

  // Cleanup function to clear timeout when component unmounts
  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  return { debouncedUpdate, cleanup };
};
